// ExportPdfModal.jsx
import React, { useState } from "react";
import { isVideo } from "./usePdfExport";

const extMeta = (ext = "") => {
  const e = ext.toLowerCase();
  if (e === "pdf")                                          return { bg:"#fce7f3", tx:"#9d174d", bd:"#f9a8d4", icon:"📄" };
  if (e === "docx" || e === "doc" || e === "word")          return { bg:"#ede9fe", tx:"#5b21b6", bd:"#c4b5fd", icon:"📝" };
  if (e === "xlsx" || e === "xls" || e === "csv")           return { bg:"#d1fae5", tx:"#065f46", bd:"#6ee7b7", icon:"📊" };
  if (e === "ppt"  || e === "pptx")                         return { bg:"#ffedd5", tx:"#9a3412", bd:"#fdba74", icon:"📑" };
  if (["png","jpg","jpeg","gif","webp"].includes(e))        return { bg:"#dbeafe", tx:"#1e40af", bd:"#93c5fd", icon:"🖼️" };
  return                                                           { bg:"#f1f5f9", tx:"#475569", bd:"#cbd5e1", icon:"📎" };
};

/**
 * ExportPdfModal
 *
 * Props:
 *   open        boolean
 *   mode        "pdf" | "pdf-attach"
 *   documents   Array  – docudatas.documents
 *   loading     boolean
 *   progress    number 0-100
 *   onClose     () => void
 *   onConfirm   (selectedDocIds: string[]) => void
 */
export default function ExportPdfModal({
  open, mode, documents = [],
  loading, progress, onClose, onConfirm,
}) {
  const nonVideo = documents.filter((d) => !isVideo(d.extension));
  const videoDoc = documents.filter((d) =>  isVideo(d.extension));

  const [sel, setSel] = useState(() => new Set(nonVideo.map((d) => String(d.id))));

  const toggle = (id) =>
    setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleAll = () =>
    setSel((p) =>
      p.size === nonVideo.length ? new Set() : new Set(nonVideo.map((d) => String(d.id)))
    );

  if (!open) return null;

  const progressLabel =
    progress < 45 ? "Rendering article…"   :
    progress < 70 ? "Building PDF…"         :
    progress < 95 ? "Adding attachments…"   :
                    "Finalising…";

  return (
    <div
      onClick={(e) => !loading && e.target === e.currentTarget && onClose()}
      style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,.5)",
        display:"flex", alignItems:"center", justifyContent:"center",
        zIndex:9999, backdropFilter:"blur(3px)", fontFamily:"inherit",
      }}
    >
      <div
        style={{
          background:"#fff", borderRadius:16, width:460, maxWidth:"95vw",
          maxHeight:"90vh", display:"flex", flexDirection:"column",
          boxShadow:"0 20px 60px rgba(0,0,0,.22)", overflow:"hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 100%)",
            padding:"1.25rem 1.5rem",
            display:"flex", alignItems:"flex-start", justifyContent:"space-between",
          }}
        >
          <div>
            <h2 style={{ margin:0, fontSize:16, fontWeight:700, color:"#fff" }}>
              {mode === "pdf" ? "📄 Export to PDF" : "📎 PDF with Attachments"}
            </h2>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"rgba(255,255,255,.6)" }}>
              {mode === "pdf"
                ? "The full article will be captured as a paginated PDF."
                : "Article + selected non-video files will be included."}
            </p>
          </div>
          {!loading && (
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background:"none", border:"none", color:"rgba(255,255,255,.65)", fontSize:22, cursor:"pointer", lineHeight:1, padding:"0 4px" }}
            >×</button>
          )}
        </div>

        {/* Body */}
        <div style={{ padding:"1.25rem 1.5rem", overflowY:"auto", flex:1 }}>

          {/* Article — always included */}
          <p style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".06em", margin:"0 0 6px" }}>
            Always included
          </p>
          <div
            style={{
              display:"flex", alignItems:"center", gap:10,
              padding:"10px 14px", borderRadius:10,
              border:"1.5px solid #2563eb", background:"#eff6ff",
              marginBottom:"1.25rem",
            }}
          >
            <span style={{ fontSize:20 }}>📰</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#1e3a8a" }}>Article content</div>
              <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Full body, metadata and formatting</div>
            </div>
            <span
              style={{
                width:20, height:20, borderRadius:"50%", background:"#2563eb",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, color:"#fff", flexShrink:0,
              }}
            >✓</span>
          </div>

          {/* Attachments list — only in pdf-attach mode */}
          {mode === "pdf-attach" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <p style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".06em", margin:0 }}>
                  Attachments ({documents.length})
                </p>
                {nonVideo.length > 0 && (
                  <button
                    onClick={toggleAll}
                    disabled={loading}
                    style={{ background:"none", border:"none", fontSize:11, color:"#2563eb", cursor:"pointer", fontWeight:600, padding:0 }}
                  >
                    {sel.size === nonVideo.length ? "Deselect all" : "Select all"}
                  </button>
                )}
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>

                {/* Selectable non-video files */}
                {nonVideo.map((doc) => {
                  const id = String(doc.id);
                  const on = sel.has(id);
                  const m  = extMeta(doc.extension);
                  return (
                    <div
                      key={id}
                      onClick={() => !loading && toggle(id)}
                      style={{
                        display:"flex", alignItems:"center", gap:10,
                        padding:"9px 12px", borderRadius:10,
                        border: on ? `1.5px solid ${m.bd}` : "1px solid #e5e7eb",
                        background: on ? m.bg : "#fafafa",
                        cursor: loading ? "not-allowed" : "pointer",
                        transition:"all .12s",
                      }}
                    >
                      <span style={{ fontSize:18, flexShrink:0 }}>{m.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:500, color:"#111", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {doc.document_name || doc.name}
                        </div>
                        <span
                          style={{
                            fontSize:10, fontWeight:700,
                            background:m.bg, color:m.tx,
                            padding:"1px 6px", borderRadius:4,
                            border:`1px solid ${m.bd}`,
                            display:"inline-block", marginTop:2,
                          }}
                        >
                          {(doc.extension || "FILE").toUpperCase()}
                        </span>
                      </div>
                      <div
                        style={{
                          width:18, height:18, borderRadius:"50%",
                          background: on ? "#2563eb" : "#e2e8f0",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          flexShrink:0, transition:"all .12s",
                        }}
                      >
                        {on && <span style={{ color:"#fff", fontSize:10 }}>✓</span>}
                      </div>
                    </div>
                  );
                })}

                {/* Video files — disabled */}
                {videoDoc.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"9px 12px", borderRadius:10,
                      border:"1px solid #fecaca", background:"#fff5f5",
                      opacity:.65, cursor:"not-allowed",
                    }}
                  >
                    <span style={{ fontSize:18 }}>🎬</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {doc.document_name || doc.name}
                      </div>
                      <div style={{ fontSize:11, color:"#dc2626", marginTop:2, fontWeight:500 }}>
                        🚫 Video — excluded from PDF export
                      </div>
                    </div>
                    <span style={{ fontSize:10, color:"#dc2626", border:"1px solid #fca5a5", padding:"2px 6px", borderRadius:4, fontWeight:700, background:"#fee2e2", flexShrink:0 }}>
                      {(doc.extension || "VIDEO").toUpperCase()}
                    </span>
                  </div>
                ))}

                {documents.length === 0 && (
                  <div style={{ textAlign:"center", padding:"1.5rem", color:"#94a3b8", fontSize:13 }}>
                    No attachments on this article.
                  </div>
                )}
              </div>
            </>
          )}

          {/* Progress bar */}
          {loading && (
            <div style={{ marginTop:"1.25rem" }}>
              <div style={{ height:6, background:"#e2e8f0", borderRadius:3, overflow:"hidden" }}>
                <div
                  style={{
                    height:"100%", width:`${progress}%`,
                    background:"linear-gradient(90deg,#1e3a8a,#2563eb,#7c3aed)",
                    borderRadius:3, transition:"width .3s ease",
                  }}
                />
              </div>
              <div style={{ fontSize:12, color:"#6b7280", marginTop:6, textAlign:"center" }}>
                {progressLabel} {Math.round(progress)}%
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding:"1rem 1.5rem", borderTop:"1px solid #f0f0f0",
            display:"flex", gap:8, justifyContent:"flex-end",
            background:"#fafafa",
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding:"8px 20px", borderRadius:8, border:"1px solid #e2e8f0",
              background:"#fff", fontSize:13, fontWeight:500,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily:"inherit", color:"#374151",
            }}
          >
            Cancel
          </button>
          <button
            disabled={loading}
            onClick={() => onConfirm(Array.from(sel))}
            style={{
              padding:"8px 22px", borderRadius:8, border:"none",
              background: loading ? "#94a3b8" : "#c32a7c",
              color:"#fff", fontSize:13, fontWeight:600,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily:"inherit",
              display:"flex", alignItems:"center", gap:6,
            }}
          >
            {loading ? "⟳ Generating…" : "⬇ Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}